import type { Letter } from './core';

interface RainRendererOptions {
  floor: number;
  doc?: Document;
}

const LETTER_CLASS = 'rain-letter';
const LAYER_CLASS = 'rain-layer rain-layer--letters';
const SPLASH_LAYER_CLASS = 'rain-layer rain-layer--splash';
const SPLASH_CLASS = 'rain-splash';

export class RainRenderer {
  private readonly stage: HTMLElement;
  private readonly floor: number;
  private readonly doc: Document;
  private readonly layer: HTMLElement;
  private readonly splashLayer: HTMLElement;
  private readonly hitLayer: HTMLElement;
  private readonly nodes = new Map<string, HTMLElement>();
  private introSeen: boolean;

  constructor(stage: HTMLElement, options: RainRendererOptions) {
    this.stage = stage;
    this.floor = Math.max(options.floor, 0.0001);
    this.doc = options.doc ?? stage.ownerDocument ?? document;
    this.introSeen = stage.dataset.introShown === 'true';

    stage.classList.add('rain-stage');
    stage.setAttribute('role', 'presentation');
    stage.setAttribute('aria-hidden', 'true');
    stage.innerHTML = '';

    if (!this.introSeen) {
      stage.classList.add('rain-stage--intro');
    }

    this.splashLayer = this.doc.createElement('div');
    this.splashLayer.className = SPLASH_LAYER_CLASS;
    stage.append(this.splashLayer);

    this.hitLayer = this.doc.createElement('div');
    this.hitLayer.className = 'rain-layer rain-layer--hit';
    stage.append(this.hitLayer);

    this.layer = this.doc.createElement('div');
    this.layer.className = LAYER_CLASS;
    stage.append(this.layer);
    stage.classList.add('rain-stage--empty');
  }

  reconcile(letters: readonly Letter[]): void {
    const nextIds = new Set<string>();

    for (const letter of letters) {
      nextIds.add(letter.id);
      let node = this.nodes.get(letter.id);
      if (!node) {
        node = this.createLetterNode(letter);
        this.nodes.set(letter.id, node);
        this.layer.append(node);
      }
      this.updateLetterNode(node, letter);
    }

    for (const [id, node] of this.nodes) {
      if (nextIds.has(id)) {
        continue;
      }
      node.remove();
      this.nodes.delete(id);
    }

    if (!this.introSeen && this.nodes.size > 0) {
      this.introSeen = true;
      this.stage.classList.remove('rain-stage--intro');
      this.stage.dataset.introShown = 'true';
    }

    this.stage.classList.toggle('rain-stage--empty', this.nodes.size === 0);
  }

  clear(): void {
    for (const node of this.nodes.values()) {
      node.remove();
    }
    this.nodes.clear();
    this.splashLayer.innerHTML = '';
    this.hitLayer.innerHTML = '';
    this.stage.classList.add('rain-stage--empty');
  }

  triggerSplash(letter: Letter): void {
    const splash = this.doc.createElement('span');
    splash.className = SPLASH_CLASS;
    const xPercent = letter.x * 100;
    splash.style.setProperty('--x', `${xPercent.toFixed(3)}%`);
    this.splashLayer.append(splash);
    splash.addEventListener(
      'animationend',
      () => {
        splash.remove();
      },
      { once: true }
    );

    // Keep recent splashes bounded to avoid DOM buildup.
    while (this.splashLayer.childElementCount > 6) {
      const first = this.splashLayer.firstElementChild;
      if (first) {
        first.remove();
      } else {
        break;
      }
    }
  }

  triggerHit(letter: Letter, color: string): void {
    const effect = this.doc.createElement('span');
    effect.className = 'rain-hit';
    effect.textContent = letter.ch.toUpperCase();
    const xPercent = letter.x * 100;
    const clampedY = Math.min(letter.y / this.floor, 1);
    const yPercent = clampedY * 100;
    effect.style.setProperty('--x', `${xPercent.toFixed(3)}%`);
    effect.style.setProperty('--y', `${yPercent.toFixed(3)}%`);
    effect.style.setProperty('--hit-color', color);
    this.hitLayer.append(effect);
    effect.addEventListener(
      'animationend',
      () => {
        effect.remove();
      },
      { once: true }
    );

    while (this.hitLayer.childElementCount > 10) {
      const first = this.hitLayer.firstElementChild;
      if (!first) {
        break;
      }
      first.remove();
    }
  }

  private createLetterNode(letter: Letter): HTMLElement {
    const node = this.doc.createElement('span');
    node.className = LETTER_CLASS;
    node.dataset.letterId = letter.id;
    node.textContent = letter.ch.toUpperCase();
    return node;
  }

  private updateLetterNode(node: HTMLElement, letter: Letter): void {
    node.textContent = letter.ch.toUpperCase();
    const xPercent = letter.x * 100;
    const clampedY = Math.min(letter.y / this.floor, 1);
    const yPercent = clampedY * 100;
    node.style.setProperty('--x', `${xPercent.toFixed(3)}%`);
    node.style.setProperty('--y', `${yPercent.toFixed(3)}%`);
    node.dataset.alive = String(letter.alive);
  }
}
